import React, { useMemo, useState } from "react";
import { Calculator } from "lucide-react";
import { Input } from "./ui/input";

const TERMS = [6, 12, 18, 24, 36, 48];
const APR_PRESETS = [
  { label: "0% promo", value: 0 },
  { label: "9.99%", value: 9.99 },
  { label: "14.99%", value: 14.99 },
  { label: "19.99%", value: 19.99 },
];

const fmt = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function FinancingCalculator() {
  const [amount, setAmount] = useState(2500);
  const [months, setMonths] = useState(12);
  const [apr, setApr] = useState(0);

  const { monthly, total, interest } = useMemo(() => {
    const p = Math.max(0, Number(amount) || 0);
    const n = months;
    const r = (Number(apr) || 0) / 100 / 12;
    let m;
    if (r === 0) m = p / n;
    else m = (p * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    const tot = m * n;
    return { monthly: m, total: tot, interest: Math.max(0, tot - p) };
  }, [amount, months, apr]);

  return (
    <div className="bg-white rounded-[2rem] border border-slate-100 p-8 md:p-12 grid lg:grid-cols-2 gap-10" data-testid="financing-calculator">
      <div>
        <div className="flex items-center gap-2.5 mb-6">
          <Calculator className="h-5 w-5 text-iri-mid" strokeWidth={1.5} />
          <h2 className="font-heading text-2xl md:text-3xl font-light text-ink">Estimate your monthly payment</h2>
        </div>

        <div className="space-y-6">
          <div>
            <div className="flex items-center justify-between">
              <label htmlFor="calc-amount" className="text-sm font-medium text-ink">Treatment amount</label>
              <span className="text-sm text-iri-mid font-medium" data-testid="calc-amount-value">{fmt(amount)}</span>
            </div>
            <input
              id="calc-amount"
              type="range"
              min="250"
              max="20000"
              step="50"
              value={amount}
              onChange={(e) => setAmount(Number(e.target.value))}
              aria-label="Treatment amount"
              data-testid="calc-amount-range"
              className="w-full mt-3 accent-[#8EC5FC]"
            />
            <Input
              type="number"
              min="0"
              value={amount}
              onChange={(e) => setAmount(Math.max(0, Number(e.target.value) || 0))}
              aria-label="Treatment amount in dollars"
              data-testid="calc-amount-input"
              className="mt-3 rounded-lg h-11 w-40"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-ink">Term</label>
            <div className="flex flex-wrap gap-2 mt-3" role="group" aria-label="Repayment term in months">
              {TERMS.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setMonths(t)}
                  aria-pressed={months === t}
                  data-testid={`calc-term-${t}`}
                  className={`rounded-full px-4 py-2 text-sm transition-colors ${months === t ? "iri-gradient text-ink font-medium" : "border border-slate-200 text-[#5C6664] hover:border-slate-300"}`}
                >
                  {t} mo
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-ink">Estimated APR</label>
            <div className="flex flex-wrap gap-2 mt-3" role="group" aria-label="Estimated annual percentage rate">
              {APR_PRESETS.map((a) => (
                <button
                  key={a.value}
                  type="button"
                  onClick={() => setApr(a.value)}
                  aria-pressed={apr === a.value}
                  data-testid={`calc-apr-${a.value}`}
                  className={`rounded-full px-4 py-2 text-sm transition-colors ${apr === a.value ? "iri-gradient text-ink font-medium" : "border border-slate-200 text-[#5C6664] hover:border-slate-300"}`}
                >
                  {a.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="iri-gradient rounded-3xl p-8 md:p-10 flex flex-col justify-center">
        <p className="text-ink/70 text-sm">Estimated monthly payment</p>
        <p className="font-heading text-6xl font-light text-ink mt-1" data-testid="calc-monthly">
          ${monthly.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          <span className="text-2xl">/mo</span>
        </p>
        <div className="mt-6 space-y-2 text-sm">
          <div className="flex items-center justify-between text-ink/80">
            <span>Over</span><span data-testid="calc-summary-term">{months} months</span>
          </div>
          <div className="flex items-center justify-between text-ink/80">
            <span>Total interest</span><span data-testid="calc-interest">{fmt(interest)}</span>
          </div>
          <div className="flex items-center justify-between text-ink font-medium pt-2 border-t border-ink/15">
            <span>Total paid</span><span data-testid="calc-total">{fmt(total)}</span>
          </div>
        </div>
        <p className="text-[11px] text-ink/60 mt-6 leading-relaxed">
          Estimate only. Actual rate, term and monthly payment depend on the financing partner and your approved credit. Many plans offer 0% APR for qualified applicants.
        </p>
      </div>
    </div>
  );
}
